import React, { createContext, useState, useContext, useEffect } from 'react';
import cartService from '../services/cart';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [loading, setLoading] = useState(false);
    const [cartItems, setCartItems] = useState(() => {
        if (!isAuthenticated) {
            const localCart = localStorage.getItem('cart');
            if (localCart) {
                try {
                    const parsed = JSON.parse(localCart);
                    if (Array.isArray(parsed)) {
                        return parsed;
                    }
                } catch {}
            }
        }
        return [];
    });

    const loadCart = async () => {
        if (isAuthenticated) {
            try {
                setLoading(true);
                const res = await cartService.getCart();
                if (res && res.items) {
                    const formattedItems = res.items.map(item => ({
                        id: item._id,
                        productId: item.product?._id || item.product,
                        name: item.product?.name || 'Product',
                        image: item.product?.images?.[0] || 'https://via.placeholder.com/150',
                        price: item.price,
                        quantity: item.quantity,
                        options: { size: item.size, color: item.color },
                        maxQuantity: item.product?.stockQuantity || 10,
                        inStock: item.product?.inStock !== false
                    }));
                    setCartItems(formattedItems);
                } else {
                    setCartItems([]);
                }
            } catch (err) {
                console.error('Failed to fetch cart', err);
            } finally {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        loadCart();
    }, [isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated) {
            localStorage.setItem('cart', JSON.stringify(cartItems));
        }
    }, [cartItems, isAuthenticated]);

    const addToCart = async (product, quantity = 1, options = {}) => {
        if (!isAuthenticated) {
            setCartItems(prevItems => {
                const existingIndex = prevItems.findIndex(i =>
                    i.productId === (product._id || product.id) &&
                    JSON.stringify(i.options) === JSON.stringify(options)
                );
                if (existingIndex > -1) {
                    const newItems = [...prevItems];
                    newItems[existingIndex].quantity += quantity;
                    return newItems;
                }
                return [...prevItems, {
                    id: Date.now().toString(),
                    productId: product._id || product.id,
                    name: product.name,
                    image: product.images?.[0] || product.image || 'https://via.placeholder.com/150',
                    price: product.price,
                    quantity,
                    options,
                    maxQuantity: product.stockQuantity || 10,
                    inStock: product.inStock !== false
                }];
            });
            toast.success('Added to cart', { id: 'cart-action-success' });
            return;
        }
        try {
            setLoading(true);
            await cartService.addToCart(product._id || product.id, quantity, {
                size: options.size,
                color: options.color
            });
            await loadCart();
            toast.success('Added to cart', { id: 'cart-action-success' });
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to add to cart', { id: 'cart-action-error' });
        } finally {
            setLoading(false);
        }
    };

    const updateQuantity = async (itemId, newQuantity) => {
        if (newQuantity < 1) return removeFromCart(itemId);
        if (!isAuthenticated) {
            setCartItems(prev => prev.map(item => item.id === itemId ? { ...item, quantity: newQuantity } : item));
            return;
        }
        try {
            setLoading(true);
            await cartService.updateCartItem(itemId, newQuantity);
            await loadCart();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update quantity', { id: 'cart-action-error' });
        } finally {
            setLoading(false);
        }
    };

    const removeFromCart = async (itemId) => {
        if (!isAuthenticated) {
            setCartItems(prev => prev.filter(item => item.id !== itemId));
            toast.success('Removed from cart', { id: 'cart-action-success' });
            return;
        }
        try {
            setLoading(true);
            await cartService.removeFromCart(itemId);
            await loadCart();
            toast.success('Removed from cart', { id: 'cart-action-success' });
        } catch (err) {
            toast.error('Failed to remove item', { id: 'cart-action-error' });
        } finally {
            setLoading(false);
        }
    };

    const clearCart = async () => {
        if (!isAuthenticated) {
            setCartItems([]);
            localStorage.removeItem('cart');
            return;
        }
        try {
            setLoading(true);
            await cartService.clearCart();
            await loadCart();
        } catch (err) {
            toast.error('Failed to clear cart', { id: 'cart-action-error' });
        } finally {
            setLoading(false);
        }
    };

    const cartTotals = {
        subtotal: cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0),
        itemsCount: cartItems.reduce((sum, item) => sum + item.quantity, 0),
        uniqueItems: cartItems.length
    };

    return (
        <CartContext.Provider value={{
            cartItems,
            loading,
            cartTotals,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart
        }}>
            {children}
        </CartContext.Provider>
    );
};

export default CartContext;